import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { useRobots } from "@/components/seo/Seo";
import { motion } from "framer-motion";
import { BadgeCheck, FileDown, Loader2, QrCode } from "lucide-react";
import MilesLogo from "@/components/Logo";

const money = (v, lang) => new Intl.NumberFormat(lang === "en" ? "en-CH" : lang === "de" ? "de-CH" : "fr-CH",
  { style: "currency", currency: "CHF" }).format(v || 0);

const T = {
  fr: { title: "Votre facture MILES", due: "Échéance", balance: "Solde à payer", paid_t: "Facture soldée",
    paid_d: "Merci — le règlement est complet.", download: "Télécharger le PDF (avec Swiss QR Code)",
    how: "Comment payer", how_d: "Ouvrez votre application bancaire, scannez le Swiss QR Code du PDF : toutes les informations sont pré-remplies.",
    iban: "IBAN", ref: "Référence", beneficiary: "Bénéficiaire", notfound: "Document introuvable" },
  en: { title: "Your MILES invoice", due: "Due date", balance: "Balance due", paid_t: "Invoice settled",
    paid_d: "Thank you — payment is complete.", download: "Download the PDF (with Swiss QR Code)",
    how: "How to pay", how_d: "Open your banking app and scan the Swiss QR Code in the PDF: everything is pre-filled.",
    iban: "IBAN", ref: "Reference", beneficiary: "Beneficiary", notfound: "Document not found" },
  de: { title: "Ihre MILES-Rechnung", due: "Fälligkeit", balance: "Offener Betrag", paid_t: "Rechnung beglichen",
    paid_d: "Danke — die Zahlung ist vollständig.", download: "PDF herunterladen (mit Swiss QR Code)",
    how: "So zahlen Sie", how_d: "Öffnen Sie Ihre Banking-App und scannen Sie den Swiss QR Code im PDF: alles ist vorausgefüllt.",
    iban: "IBAN", ref: "Referenz", beneficiary: "Begünstigter", notfound: "Dokument nicht gefunden" },
};

export default function InvoicePublic() {
  const { token } = useParams();
  useRobots("noindex, nofollow");
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    axios.get(`/api/public/factures/${token}`).then((r) => setData(r.data)).catch(() => setError(true));
  }, [token]);

  if (error) return <Shell><p className="text-center text-slate">Document introuvable.</p></Shell>;
  if (!data) return <Shell><Loader2 className="mx-auto h-6 w-6 animate-spin text-miles" strokeWidth={2} /></Shell>;

  const { invoice: inv, company, bank } = data;
  const lang = T[inv.language] ? inv.language : "fr";
  const t = T[lang];
  const paid = (inv.balance || 0) <= 0;

  return (
    <Shell>
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
        className="rounded-[1.75rem] border border-ink/[0.07] bg-white p-6 shadow-[0_24px_80px_-48px_rgba(16,24,40,0.25)] sm:p-10">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-miles">{t.title}</p>
            <h1 className="mt-2 text-2xl font-bold tracking-tight text-ink sm:text-3xl">{inv.number}</h1>
            <p className="mt-1 text-sm text-slate">{inv.customer?.name}</p>
          </div>
          <span className="shrink-0 rounded-full bg-cloud px-3 py-1.5 text-xs font-medium text-slate">{t.due} : {inv.due_date}</span>
        </div>

        {inv.service_info?.route && (
          <div className="mt-6 rounded-2xl bg-cloud p-4 text-sm">
            <p className="font-semibold text-ink">{inv.service_info.route}</p>
            {inv.service_info.ride_date && <p className="mt-1 text-slate">{inv.service_info.ride_date}</p>}
          </div>
        )}

        <div className="mt-6 space-y-3">
          {(inv.lines || []).map((l, i) => (
            <div key={i} className="flex items-baseline justify-between gap-4 text-sm">
              <span className="text-ink">{l.description}</span>
              <span className="shrink-0 font-mono font-medium text-ink">{l.unit_price === 0 ? "—" : money(l.total, lang)}</span>
            </div>
          ))}
        </div>

        <div className="mt-6 flex items-center justify-between rounded-2xl bg-miles px-5 py-4 text-white">
          <span className="font-semibold">{t.balance}</span>
          <span className="font-mono text-xl font-bold" data-testid="public-invoice-balance">{money(inv.balance, lang)}</span>
        </div>

        {paid ? (
          <div className="mt-6 flex items-start gap-3 rounded-2xl bg-emerald-50 p-5" data-testid="invoice-paid">
            <BadgeCheck className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" strokeWidth={1.5} />
            <div>
              <p className="font-semibold text-emerald-800">{t.paid_t}</p>
              <p className="mt-0.5 text-sm text-emerald-700">{t.paid_d}</p>
            </div>
          </div>
        ) : (
          <div className="mt-6 rounded-2xl border border-ink/[0.07] p-5">
            <p className="flex items-center gap-2 text-sm font-semibold text-ink">
              <QrCode className="h-4 w-4 text-miles" strokeWidth={1.5} /> {t.how}</p>
            <p className="mt-1.5 text-sm text-slate">{t.how_d}</p>
            <div className="mt-4 space-y-1.5 text-sm">
              <div className="flex justify-between"><span className="text-slate">{t.beneficiary}</span><span className="font-medium text-ink">{bank.account_holder}</span></div>
              <div className="flex justify-between"><span className="text-slate">{t.iban}</span><span className="font-mono text-xs font-medium text-ink">{bank.iban}</span></div>
              {inv.reference && <div className="flex justify-between"><span className="text-slate">{t.ref}</span><span className="font-mono text-xs font-medium text-ink">{inv.reference.replace(/(.{4})/g, "$1 ").trim()}</span></div>}
            </div>
          </div>
        )}

        <a href={`/api/public/factures/${token}/pdf`} data-testid="invoice-pdf-download"
          className="mt-6 flex h-14 w-full items-center justify-center gap-2 rounded-2xl bg-ink text-[15px] font-semibold text-white transition-colors hover:bg-miles">
          <FileDown className="h-4 w-4" strokeWidth={1.5} /> {t.download}
        </a>

        <p className="mt-8 text-center text-xs text-slate/70">
          {company.brand_name} · {company.email} · {company.phone}
        </p>
      </motion.div>
    </Shell>
  );
}

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-cloud px-4 py-10">
      <div className="mx-auto max-w-2xl">
        <div className="mb-8 flex justify-center"><MilesLogo className="h-8" variant="blue" /></div>
        {children}
      </div>
    </div>
  );
}
